import React, {lazy} from 'react';
import {IndexRoute, Redirect, Route} from 'react-router';

import {resumeSession} from '@albert-io/mandark/authentication';

import {TranslationSandbox} from 'client/TranslationSandbox/TranslationSandbox';

import constants from 'client/constants';
import {callAction} from 'client/framework';
import {requireSessionWithLoginPrompt} from 'lib/decorators/requireSession';
import App from './App.react';
import {appActions} from './AppActions';

import Register from 'client/static/UserActions/Register/Register.react';
import ErrorPage from './static/Error/ErrorPage.react';
import ExpiredSessionPage from './static/ExpiredSession/ExpiredSessionPage.react';
import SoftwareConsiderations from './static/SoftwareConsiderations/SoftwareConsiderations.react';

import ContentDiscoveryRoutes from 'client/components/ContentDiscovery/routes';
import classesRoutes from './Classrooms/routes';

import {DennisRoutes} from './Dennis/dynamic.routes';
import {MarketplaceRoutes} from './Marketplace/dynamic.routes';
import reportsRoutes from './Reports/routes';
import schoolAdministrationRoutes from './SchoolAdministration/routes';

import sessionStore from 'client/Session/SessionStore';
import {getUserTypeRedirectTarget} from 'client/User/UserRedirectUtil';

import {queryTypes} from 'client/SubjectPracticeView/subjectPracticeView.queries';
import {singleQuestionPath} from './SubjectPracticeView/subjectPracticeView.utils';

import GlobalAssignments from 'client/Assignments/GlobalAssignments/GlobalAssignments.react';
import Coppa from 'client/Coppa/Coppa.react';
import ConfirmEmail from 'client/static/UserActions/ConfirmEmail/ConfirmEmail.react';
import ForgotPassword from './ForgotPassword/ForgotPassword';
import LogInPage from './LogIn/LogInPage';
import ResetPassword from './ResetPassword/ResetPassword';
import SignUpPage from './SignUp/NewSignUp/SignUpPage';
import SocialLanding from './Social/Landing.react';
import SubjectLibrary from './SubjectLibrary/SubjectLibrary.react';
import {SubjectView} from './SubjectView/V2/SubjectView';
import {subjectPageOnEnter} from './SubjectView/V2/subjectPageOnEnter';

import FRQGuideRouter from './FreeResponseQuestion/FRQGuideRouter';
import FRQViewRouter from './FreeResponseQuestion/FRQViewRouter';
import StudyGuide from './StudyGuide/StudyGuide.react';
import SubjectFAQList from './SubjectFAQList/SubjectFAQList';

import AssignmentsFoldersWrapper from 'client/components/AssignmentsFolders/AssignmentsFoldersWrapper.react';
import SubjectPracticeView from './SubjectPracticeView/SubjectPracticeView.react';
import StudentHomepageRoutes from './StudentHomepage/routes';

const Account = lazy(() => import('client/AccountSettings/index.react'));
const AccountProfile = lazy(
  () => import('client/AccountSettings/AccountProfile/AccountProfile.react')
);
const Subscriptions = lazy(
  () => import('client/AccountSettings/Subscriptions/Subscriptions.react')
);
const PasswordAndSettings = lazy(
  () => import('client/AccountSettings/PasswordAndSettings/PasswordAndSettings')
);
const DeactivateAccount = lazy(
  () => import('client/AccountSettings/DeactivateAccount/DeactivateAccount.react')
);

const UserDashboard = lazy(() => import('./UserDashboard/UserDashboard'));

const StudentDetailView = lazy(() => import('./Student/StudentDetail.react'));

const Folders = lazy(() => import('./Folders/V2/Folders.react'));
const AssignmentDrafts = lazy(() => import('client/AssignmentDrafts/AssignmentDrafts.react'));

const TeacherAssignmentPracticeView = lazy(
  () =>
    import('client/Classrooms/Classroom/Teacher/Assignments/TeacherAssignmentPracticeView/TeacherAssignmentPracticeView.react')
);
const ViewStudentSubmission = lazy(
  () =>
    import('client/Classrooms/Classroom/Teacher/Assignments/ViewStudentSubmission/ViewStudentSubmission.react')
);

const AdaptiveSubjects = lazy(() => import('client/AdaptiveSubjects/AdaptiveSubjects'));
const AdaptiveDiagnosticAssignPage = lazy(
  () => import('client/AdaptiveDiagnostics/AssignPage/AdaptiveDiagnosticAssignPage')
);

/**
 * Practice Views
 */
const AssignmentPracticeView = lazy(
  () => import('./AssignmentPracticeView/AssignmentPracticeView.react')
);
const FolderPracticeView = lazy(() => import('./FolderPracticeView/FolderPracticeView.react'));
const AdaptivePracticeView = lazy(() => import('./AdaptivePracticeView/AdaptivePracticeView'));
const DiagnosticPracticeView = lazy(
  () => import('./DiagnosticPracticeView/DiagnosticPracticeView')
);
const TeacherDiagnosticManagement = lazy(
  () => import('./AdaptiveDiagnostics/Management/TeacherDiagnosticManagement')
);

const Sandbox = lazy(() => import(/* webpackChunkName: "Sandbox" */ './Sandbox/Sandbox.react'));
const CommonBankQuestion = lazy(
  () => import('./components/QuestionTypes/CommonBank/CommonBankQuestion')
);
const OrderingBankQuestion = lazy(
  () => import('./components/QuestionTypes/CommonBank/OrderingBankQuestion')
);
const GeneratedStories = lazy(
  () => import(/* webpackChunkName: "Stories" */ './GeneratedStories/GeneratedStories.react')
);

function redirectOnEnterIfAuthenticated(nextState, replace, callback) {
  if (sessionStore.hasValidSession()) {
    getUserTypeRedirectTarget().then((redirectPath) => {
      replace(redirectPath);
      callback();
    });
  } else {
    callback();
  }
}

function updateGlobalRouterProps(routerProps, prevRouterProps = {}) {
  callAction(appActions.SET_ROUTER_PROPS, {routerProps, prevRouterProps});
  const zone = routerProps.location.pathname.startsWith('/dennis')
    ? constants.ZONES.DENNIS
    : constants.ZONES.ALBERT;
  callAction(appActions.SET_ZONE, zone);
}

const AssignmentsRoute = () => (
  <AssignmentsFoldersWrapper>
    <GlobalAssignments />
  </AssignmentsFoldersWrapper>
);

const FoldersRoute = () => (
  <AssignmentsFoldersWrapper>
    <Folders />
  </AssignmentsFoldersWrapper>
);

const TeacherDiagnosticsRoute = () => (
  <AssignmentsFoldersWrapper>
    <TeacherDiagnosticManagement />
  </AssignmentsFoldersWrapper>
);

export default function Routes() {
  return (
    <Route
      component={App}
      path='/'
      onEnter={async (nextState, replace, callback) => {
        updateGlobalRouterProps(nextState);
        if (nextState.location.query.state === 'error') {
          callback();
          return;
        }
        await resumeSession();
        callback();
      }}
      onChange={(prevState, nextState) => {
        updateGlobalRouterProps(nextState, prevState);
      }}
    >
      <Route component={ExpiredSessionPage} path='session/expired' />

      {MarketplaceRoutes}

      <Route component={Register} path='register' />

      {StudentHomepageRoutes}

      {/* Assignments */}
      <Route path='assignments' component={requireSessionWithLoginPrompt()(AssignmentsRoute)} />
      <Route
        path='diagnostics'
        component={requireSessionWithLoginPrompt()(TeacherDiagnosticsRoute)}
      />
      <Route
        path='saved'
        component={requireSessionWithLoginPrompt()(FoldersRoute)}
        withWhiteBackground
      />
      <Route
        path='drafts'
        component={requireSessionWithLoginPrompt()(AssignmentDrafts)}
        withWhiteBackground
      />
      <Redirect from='assignment/folders/:assignmentId' to='folder/:assignmentId' />
      <Redirect from='assignment/templates/:assignmentId' to='folder/:assignmentId' />
      <Route
        getComponent={(nextState, cb) => {
          cb(
            null,
            sessionStore.isStudent()
              ? AssignmentPracticeView
              : requireSessionWithLoginPrompt()(TeacherAssignmentPracticeView)
          );
        }}
        path='assignment/:assignmentId'
      />
      <Route
        path='classes/:classroomId/assignments/:assignmentId/student-detail/:studentId/view'
        component={ViewStudentSubmission}
      />
      <Route
        path='classes/:classroomId/assignments/:assignmentId/student-detail/:studentId/question/:questionId/view'
        component={ViewStudentSubmission}
      />

      {ContentDiscoveryRoutes}

      {/* Folder */}
      <Route
        path='/folder/:folderId'
        component={requireSessionWithLoginPrompt()(FolderPracticeView)}
      />

      {/* Subject Practice View */}
      <Route
        path={singleQuestionPath}
        queryType={queryTypes.singleQuestion}
        component={SubjectPracticeView}
        onEnter={(nextState, replace) => {
          if (!nextState.params.questionSlug) {
            replace('/error');
          }
        }}
      />
      <Route
        isTopicSummary
        path='/learn/:subjectSlug/:guideLevelSlug/topic-summary'
        queryType={queryTypes.guideLevel}
        component={SubjectPracticeView}
      />
      <Route
        path='/learn/:subjectSlug/:guideLevelSlug'
        queryType={queryTypes.guideLevel}
        component={SubjectPracticeView}
      />
      <Route
        path='/learn/:subjectSlug/:guideLevelSlug/:questionSetSlug(/:questionSlug)'
        queryType={queryTypes.guideLevel}
        component={SubjectPracticeView}
      />

      <Route path='software-considerations' component={SoftwareConsiderations} />

      <Route component={ConfirmEmail} path='confirm-email' />
      <Route component={ResetPassword} path='reset-password' withWhiteBackground />

      <Redirect from='dashboard' to='my-data' />
      <Route component={UserDashboard} path='my-data' withWhiteBackground />
      <Redirect from='dashboard/:studentId/:subjectId' to='my-data/:studentId/:subjectId' />
      <Route component={StudentDetailView} path='my-data/:studentId/:subjectId' />

      {schoolAdministrationRoutes}
      {/* Classes */}
      {classesRoutes}
      {/* Analytics Reports */}
      {reportsRoutes}

      {/* My Account */}
      <Route path='account' name='account' component={Account}>
        <Route path='profile' name='account-profile' component={AccountProfile} />
        <Route path='access' name='account-access' component={Subscriptions} />
        <Route path='settings' name='account-settings' component={PasswordAndSettings} />
      </Route>
      <Route path='account/deactivate' name='account-deactivate' component={DeactivateAccount} />
      {/* My Account End */}

      {/* Course Library Routes */}
      <Route path='subjects' component={SubjectLibrary} />
      <Redirect from='subjects/free' to='subjects' />
      <Redirect from='subjects/discover' to='subjects' />
      <Route path='subjects/adaptive-practice' component={SubjectLibrary} />
      <Route path='subjects/class-subjects' component={SubjectLibrary} content='class-subjects' />
      <Route path='/subjects/high-school(/:group(/:subgroup))' component={SubjectLibrary} />
      <Route path='/subjects/middle-school(/:group(/:subgroup))' component={SubjectLibrary} />
      <Redirect from='/subjects/college/social-sciences(/:subgroup)' to='/subjects/college' />
      <Route path='/subjects/college(/:group(/:subgroup))' component={SubjectLibrary} />
      <Redirect from='/subjects/professional(/:group(/:subgroup))' to='/subjects/college' />
      {/* Course Library Routes End */}

      {/* Adaptive Practice */}
      <Route path='/adaptive-subjects/:slug(/:guideName)' component={AdaptiveSubjects} />
      <Route
        path='/diagnostics/:diagnosticTemplateId/assign(/:diagnosticId)'
        component={AdaptiveDiagnosticAssignPage}
      />
      <Route path='/adaptive/skill/:skillId' component={AdaptivePracticeView} />
      <Route path='/adaptive/practice/:practiceId' component={AdaptivePracticeView} />
      {/* Adaptive Practice End */}

      {/* Adaptive Diagnostics (prototype) */}
      <Route path='/diagnostics/:diagnosticId' component={DiagnosticPracticeView} />
      {/* Adaptive Diagnostics End */}

      {/* Authoring - Redirect to Dennis */}
      <Redirect
        from='/authoring/queue(/:questionSetId(/:questionId))'
        to='dennis/content/queue(/:questionSetId(/:questionId))'
      />
      <Redirect from='/authoring/*' to='dennis/content/dashboard' />

      {DennisRoutes}

      <Redirect from='batch' to='dennis/batch' />
      <Redirect from='batch/:a' to='dennis/batch/:a' />
      <Redirect from='batch/:a/:b' to='dennis/batch/:a/:b' />

      <Route path='social' component={SocialLanding} />
      <Route
        name='signup'
        path='signup'
        component={SignUpPage}
        onEnter={redirectOnEnterIfAuthenticated}
      />
      <Route path='log-in' component={LogInPage} onEnter={redirectOnEnterIfAuthenticated} />
      <Route path='coppa/confirmation' component={Coppa} />
      <Route path='forgot-password' component={ForgotPassword} withWhiteBackground />

      <Route path='error' component={ErrorPage} />
      <Route path='translation-sandbox' component={TranslationSandbox} />

      <Route path='sandbox'>
        <IndexRoute component={Sandbox} />
      </Route>
      <Route path='test/commonbank' component={CommonBankQuestion} />
      <Route path='test/orderingbank' component={OrderingBankQuestion} />
      <Route component={GeneratedStories} path='storybook(/:atomicLevel(/:component(/:variant)))' />

      <Route name='subject-page' path=':slug' component={SubjectView} onEnter={subjectPageOnEnter}>
        <IndexRoute name='study-guide' component={StudyGuide} withWhiteBackground />
        <Route path='free-response' component={FRQGuideRouter} withWhiteBackground />
        <Route path='assessments' component={StudyGuide} withWhiteBackground />
        {/* legacy redirect, probably unnecessary but doesn't hurt to keep it in case any users still use this URL */}
        <Redirect from='practice-exams' to='assessments' />
        <Redirect from='faq' to='overview' />
        <Route name='subject-overview' path='overview' component={SubjectFAQList} />
        <Route
          name='free-response-view'
          path='free-response/:set(/:question)'
          component={FRQViewRouter}
        />
      </Route>

      <Route name='error-page' path='*' component={ErrorPage} />
    </Route>
  );
}
